/*
 * Getting a photo or a video ready to send.
 *
 * Two things happen here, both on the sending device, before anything is
 * written anywhere.
 *
 * A tiny preview is drawn — about sixty pixels, a couple of kilobytes — and
 * travels inside the message itself. The message replicates the moment two
 * devices meet; the file follows block by block and may take a while on a
 * phone, or wait for the sender to come back online. Until it does, the other
 * side now sees a blurred picture of what is coming instead of an empty box.
 *
 * And a large photo is re-encoded. A modern phone camera writes 3–8 MB per
 * shot, and every byte of it has to go peer to peer — for a browser, through
 * the relay's queue — to be looked at on a screen a few hundred pixels wide.
 * Past PHOTO_LIMIT the picture is redrawn at no more than MAX_SIDE pixels and
 * saved as a JPEG, which is what every messenger sends as a "photo". It also
 * turns an iPhone's HEIC, which only Apple devices can open, into something
 * every other device can. A file sent as a document is left exactly as it is.
 *
 * Everything is best effort and bounded in time. A format the browser cannot
 * decode simply goes as it was, without a preview.
 */

const THUMB_SIDE = 64
const THUMB_QUALITY = 0.6
const MAX_SIDE = 2560
const PHOTO_QUALITY = 0.85
const PHOTO_LIMIT = 1.5 * 1024 * 1024
const TIMEOUT = 5000

/**
 * @param compress  false to keep the file byte for byte (sent as a document)
 * @returns { bytes, name, mime, width, height, thumb } or null when undecodable
 */
export async function preparePhoto (bytes, { name = 'photo', mime = '', compress = true } = {}) {
  if (typeof document === 'undefined') return null
  const blob = new Blob([bytes], { type: mime || 'application/octet-stream' })
  const image = await loadImage(blob)
  if (!image) return null

  const width = image.naturalWidth || image.width
  const height = image.naturalHeight || image.height
  const out = { bytes, name, mime, width, height, thumb: null }

  out.thumb = await draw(image, width, height, THUMB_SIDE, THUMB_QUALITY)

  const heic = /image\/hei[cf]/i.test(mime) || /\.hei[cf]$/i.test(name)
  const animated = /image\/gif/i.test(mime)
  const big = bytes.byteLength > PHOTO_LIMIT || Math.max(width, height) > MAX_SIDE
  if (compress && !animated && (heic || big)) {
    const encoded = await draw(image, width, height, MAX_SIDE, PHOTO_QUALITY)
    if (encoded && (heic || encoded.byteLength < bytes.byteLength)) {
      const scale = Math.min(1, MAX_SIDE / Math.max(width, height))
      out.bytes = encoded
      out.mime = 'image/jpeg'
      out.name = name.replace(/\.[^./\\]+$/, '') + '.jpg'
      out.width = Math.round(width * scale)
      out.height = Math.round(height * scale)
    }
  }
  return out
}

/** Redraw any decodable picture as a JPEG no larger than `side` pixels. */
export async function shrinkToJpeg (bytes, { mime = '', side = 360, quality = 0.7 } = {}) {
  if (typeof document === 'undefined') return null
  const image = await loadImage(new Blob([bytes], { type: mime || 'application/octet-stream' }))
  if (!image) return null
  return draw(image, image.naturalWidth || image.width, image.naturalHeight || image.height, side, quality)
}

/** @returns { width, height, duration, thumb } — whatever the browser would say */
export async function describeVideo (bytes, { mime = '' } = {}) {
  if (typeof document === 'undefined') return {}
  const url = URL.createObjectURL(new Blob([bytes], { type: mime || 'video/mp4' }))
  const video = document.createElement('video')
  video.muted = true
  video.playsInline = true
  video.preload = 'auto'
  try {
    const loaded = await bounded((done) => {
      video.onloadedmetadata = () => done(true)
      video.onerror = () => done(false)
      video.src = url
    })
    if (!loaded) return {}

    const info = {
      width: video.videoWidth || 0,
      height: video.videoHeight || 0,
      duration: Math.round(video.duration || 0)
    }

    // A frame a little way in, rather than the first, which is black as often
    // as not.
    const at = Math.min(1, (video.duration || 0) / 3)
    const seeked = await bounded((done) => {
      video.onseeked = () => done(true)
      video.onerror = () => done(false)
      try { video.currentTime = at } catch { done(false) }
    })
    if (seeked && info.width && info.height) {
      info.thumb = await draw(video, info.width, info.height, THUMB_SIDE, THUMB_QUALITY)
    }
    return info
  } finally {
    video.removeAttribute('src')
    try { video.load() } catch {}
    URL.revokeObjectURL(url)
  }
}

function loadImage (blob) {
  const url = URL.createObjectURL(blob)
  const image = new Image()
  return bounded((done) => {
    image.onload = () => done(image)
    image.onerror = () => done(null)
    image.src = url
  }).finally(() => {
    // Revoked only once decoded: the canvas draws from the element, not the URL.
    setTimeout(() => URL.revokeObjectURL(url), 0)
  })
}

/** Draw a source at no more than `side` pixels along its longer edge, as JPEG bytes. */
async function draw (source, width, height, side, quality) {
  if (!width || !height) return null
  try {
    const scale = Math.min(1, side / Math.max(width, height))
    const canvas = document.createElement('canvas')
    canvas.width = Math.max(1, Math.round(width * scale))
    canvas.height = Math.max(1, Math.round(height * scale))
    const context = canvas.getContext('2d')
    if (!context) return null
    // White under anything transparent: JPEG has no alpha, and a PNG with a
    // clear background would otherwise come out black.
    context.fillStyle = '#fff'
    context.fillRect(0, 0, canvas.width, canvas.height)
    context.drawImage(source, 0, 0, canvas.width, canvas.height)
    const blob = await bounded((done) => canvas.toBlob(done, 'image/jpeg', quality))
    if (!blob) return null
    return new Uint8Array(await blob.arrayBuffer())
  } catch {
    // A tainted or oversized canvas, or a decoder that gave up.
    return null
  }
}

/** Resolve with whatever `start` reports, or null after TIMEOUT: none of these events is promised. */
function bounded (start) {
  return new Promise((resolve) => {
    let settled = false
    const finish = (value) => {
      if (settled) return
      settled = true
      clearTimeout(timer)
      resolve(value)
    }
    const timer = setTimeout(() => finish(null), TIMEOUT)
    try {
      start(finish)
    } catch {
      finish(null)
    }
  })
}
